import { EnergyAssetThirtyDaysPVInfoRes, getEnergyAssetThirtyDaysPVInfo, OuList } from '@/api/energyAssetOverview';
import { FC, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import styles from './index.module.scss';
import { isNil } from 'lodash-es';
import { average } from '../../utils';
import { StationHasCount } from '../..';
import { Empty, FormTitle, EchartsWithTheme, useTheme } from '@maxtropy/components';

export interface ICurrentThirtyDaysPV {
  ouInfo: OuList;
  height: string | undefined;
}

const CurrentThirtyDaysPV: FC<ICurrentThirtyDaysPV> = props => {
  const { ouInfo, height } = props;
  const stationHasCount = useContext(StationHasCount);
  const [thirtyDaysPVInfo, setThirtyDaysPVInfo] = useState<EnergyAssetThirtyDaysPVInfoRes[]>();
  const [dataLoading, setDataLoading] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (ouInfo.id) {
      setDataLoading(true);
      // 近 30 日光伏站发电量
      getEnergyAssetThirtyDaysPVInfo(ouInfo.id).then(res => {
        setThirtyDaysPVInfo(res.list ?? []);
        setDataLoading(false);
      });
    }
  }, [ouInfo]);

  const option = {
    grid: {
      left: 60,
      right: 30,
      top: 55,
      bottom: 20,
    },
    legend: {
      show: true,
      right: 20,
      top: 0,
    },
    tooltip: {
      formatter(items: any[]) {
        const { axisValue } = items[0];
        // // 根据选择的颗粒度，展示不同的tooltip
        let str: string = dayjs(axisValue, 'x').format('YYYY-MM-DD');
        const avg = average(thirtyDaysPVInfo?.map(i => i.electricityQuantity));
        const avgValue = isNaN(avg) ? '--' : avg.toFixed(2);
        str += `<br>近30日平均发电量：<span style='display:inline-block;width:86px;text-align:right;color:#59D744'>${avgValue}kWh</span>`;
        items.forEach(item => {
          const { seriesName, data, marker } = item;
          const value = !isNil(data[1]) ? `${data[1].toFixed(2)}` : '--';
          str += `<br><span style='display:inline-block;width:82px;text-align:right'>${marker}${seriesName}：</span> ${value}kWh<br>
          <span style='display:inline-block;width:80px;text-align:right'>环比：</span>
          ${
            data[2] && data[2] > 0
              ? `<span style='display:inline-block;border-radius:10px;color:#FF4D4F;width:86px;'>${
                  !isNil(data[2]) ? '+' + (Number(data[2]) * 100).toFixed(2) + '%' : '--'
                }</span>`
              : `<span style='display:inline-block;border-radius:10px;color:#59D744;width:86px;'>${
                  !isNil(data[2]) ? (Number(data[2]) * 100).toFixed(2) + '%' : '--'
                }</span>`
          }`;
        });

        return str;
      },
    },
    xAxis: {
      type: 'time',
      minInterval: 60 * 1000 * 60 * 24,
      axisLabel: {
        hideOverlap: true,
        formatter: function (e: number) {
          return dayjs(e, 'x').format('MM-DD');
        },
      },
    },
    yAxis: {
      type: 'value',
      name: 'kWh',
      nameGap: 30,
    },
    series: [
      {
        type: 'bar',
        name: '发电量',
        color: '#16DD8E',
        data: thirtyDaysPVInfo?.map(a => {
          return [a.datetime, a.electricityQuantity, a.electricityQuantityRate];
        }),
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          lineStyle: {
            type: 'dashed',
            color: '#16DD8E',
          },
          data: [
            {
              label: { formatter: `平均发电量: {c}kWh`, position: 'insideEndTop', color: '#fff', show: false },
              yAxis: average(thirtyDaysPVInfo?.map(i => i.electricityQuantity)).toFixed(2),
            },
          ],
        },
      },
    ],
  };

  return (
    <div
      className={styles.ColSty}
      style={{
        height: height,
      }}
    >
      <FormTitle title="近30日光伏站-发电量" />
      <Spin spinning={dataLoading}>
        {stationHasCount?.pvCount ? (
          <EchartsWithTheme
            option={option}
            params={{ dataZoomExit: false }}
            theme={theme}
            style={{ height: height === 'unset' ? 240 : `calc(${height} - 50px)` }} // 样式的设置width与height
          />
        ) : (
          <Empty
            style={{ height: height === 'unset' ? 240 : `calc(${height} - 50px)`, paddingTop: '10%' }}
            description={'无光伏站'}
          />
        )}
      </Spin>
    </div>
  );
};

export default CurrentThirtyDaysPV;
